import { animate, state, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { ConnectedPosition } from '@angular/cdk/overlay';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    input,
    OnChanges,
    signal,
    SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { map } from 'rxjs';

@Component({
    selector: 'pn-sidebar',
    templateUrl: './sidebar.component.html',
    styles: [``],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('expandCollapseSidebar', [
            state('expand', style({ width: '264px' })),
            state('collapse', style({ width: '96px' })),
            transition('expand <=> collapse', [animate('200ms ease-out')]),
        ]),
    ],
})
export class SidebarComponent implements OnChanges {
    public showSidebar = input<boolean>(false);
    public sidebarVisible = signal(false);
    public isExpand = signal<boolean>(true);
    private _breakpointObserver = inject(BreakpointObserver);
    private _device = inject(DeviceDetectorService);
    private _router = inject(Router);
    private _cd = inject(ChangeDetectorRef);
    private breakpointMap: Record<string, string> = {
        mobile: '(max-width: 600px)',
        xl: '(max-width: 1280px)',
        desktop: '(max-width: 1440px)',
    };
    public xlBreakpoint = toSignal(
        this._breakpointObserver
            .observe([this.breakpointMap['xl']])
            .pipe(map((value) => value.breakpoints[this.breakpointMap['xl']]))
    );
    public desktopBreakpoint = toSignal(
        this._breakpointObserver
            .observe(['(min-width: 1440px)'])
            .pipe(map((value) => value.breakpoints['(min-width: 1440px)']))
    );
    public cdkConnectedOverlayPositions: ConnectedPosition[] = [
        {
            originX: 'start',
            originY: 'center',
            overlayX: 'start',
            overlayY: 'top',
            offsetX: 63,
            offsetY: -15,
        },
    ];
    public currentUrl = this._router.url;

    constructor() {
        this._breakpointObserver
            .observe([this.breakpointMap['mobile'], this.breakpointMap['desktop']])
            .pipe(takeUntilDestroyed())
            .subscribe((value: BreakpointState) => {
                if (value.breakpoints[this.breakpointMap['mobile']]) {
                    this.isExpand.set(false);
                    this.sidebarVisible.set(false);
                    return;
                }
                this.isExpand.set(!value.breakpoints[this.breakpointMap['desktop']]);
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        const showSidebarChange = changes['showSidebar'];
        if (showSidebarChange?.currentValue) {
            this.sidebarVisible.set(true);
        }
    }

    /**
     * Handle open menu
     *
     * @return {*}
     * @memberof SidebarComponent
     */
    public handleOpenMenu(): void {
        if (!this.desktopBreakpoint()) {
            return;
        }
        this.isExpand.set(!this.isExpand());
        this._cd.detectChanges();
    }
}
